<script lang="ts">
	import Formatted from "@components/common/Formatted.svelte";
	import Icon from "@components/common/Icon.svelte";
	import Link from "@components/common/Link.svelte";
	import { ButtonVariant } from "@components/common/getButtonClass.js";
	import { isMenuActive } from "@utils/isMenuActive.js";
	import catalogueLogo from "@core/assets/icons/catalogue-logo.svg?raw";
	import chevron from "@core/assets/icons/chevron.svg?raw";
	import { Locale } from "@core/schema/Locale.js";
	import type { MenuItem } from "@core/schema/MenuItem.js";
	import { catalogueNameMap } from "@core/schema/names/catalogueNameMap.js";
	import { getCataloguePath } from "@core/schema/paths/getCataloguePath.js";
	import { getGlobalTemplateProps } from "../../contexts/getGlobalTemplateProps.js";
	import LanguagePicker from "../LanguagePicker.svelte";
	import MenuItemLink from "../MenuItemLink.svelte";
	import TextWithIcon from "../common/TextWithIcon.svelte";

	let {
		mobileOpen = $bindable(false),
		/* eslint-disable prefer-const */
		items,
		headerHeight,
		/* eslint-enable prefer-const */
	}: { mobileOpen?: boolean; items: MenuItem[]; headerHeight: number } = $props();

	const { pagePath, tenant } = getGlobalTemplateProps();
	const currentPath = pagePath[tenant.locale];

	const menuItemElements = $state<HTMLLIElement[]>([]);
	const submenuOpenMap = $state<Record<number, boolean>>({});

	$effect(() => {
		if (typeof document !== "undefined") {
			if (mobileOpen) {
				document.documentElement.style.overflow = "hidden";
			} else {
				document.documentElement.style.overflow = "";
			}
		}
		return (): void => {
			if (typeof document !== "undefined") {
				document.documentElement.style.overflow = "";
			}
		};
	});

	function checkClickOutside(event: MouseEvent): void {
		const target = event.target as HTMLElement;
		for (let index = 0; index < items.length; index++) {
			const openSubmenu = submenuOpenMap[index] ?? false;
			const menuItemElement = menuItemElements[index];
			if (openSubmenu && menuItemElement && !menuItemElement.contains(target)) {
				submenuOpenMap[index] = false;
			}
		}
	}
</script>

<svelte:window onclick={checkClickOutside} />

<div class="max-lg:fixed left-0 right-0 bottom-0 w-full pointer-events-none" style:top="{headerHeight}px">
	<div
		class={[
			"flex overflow-auto w-full justify-between lg:overflow-visible items-center flex-col lg:flex-row lg:bg-transparent bg-white gap-3 sm:gap-4 lg:gap-8 pb-4 sm:pb-6 lg:pb-0 pt-4 sm:pt-6 pointer-events-auto lg:pt-0 transition-[transform,background-color] h-full duration-100",
		]}
		class:max-lg:pointer-events-none={!mobileOpen}
		class:max-lg:translate-x-full={!mobileOpen}
	>
		<ul
			class="flex flex-col lg:items-center lg:flex-row gap-3 sm:gap-4 lg:gap-8 max-lg:items-center max-lg:text-center max-lg:w-full"
		>
			{#each items as menuItem, index (index)}
				{@const openSubmenu = submenuOpenMap[index] ?? false}
				<li
					bind:this={menuItemElements[index]}
					class={{
						"flex flex-col relative": true,
					}}
					onpointerenter={(): void => {
						if (!window.matchMedia("(pointer: coarse)").matches) {
							submenuOpenMap[index] = true;
						}
					}}
					onpointerleave={(): void => {
						if (!window.matchMedia("(pointer: coarse)").matches) {
							submenuOpenMap[index] = false;
						}
					}}
				>
					<MenuItemLink
						class="relative lg:h-full flex max-lg:text-center max-lg:justify-center lg:text-left lg:justify-end items-center gap-0.5 text-lg sm:text-xl lg:text-xs lg:font-medium font-bold lg:text-nowrap after:lg:content-[''] after:lg:absolute after:lg:left-1/2 after:lg:-translate-x-1/2 after:lg:bottom-1 after:lg:h-[1.5px] after:lg:w-[0px] after:lg:bg-zms-blue after:transition-all after:duration-300 hover:lg:after:w-full"
						{menuItem}
						onclick={(event: MouseEvent): void => {
							if (window.matchMedia("(pointer: coarse)").matches) {
								if (!openSubmenu && (menuItem.submenuItems?.length ?? 0) > 0) {
									event.preventDefault();
									submenuOpenMap[index] = !openSubmenu;
								}
							}
						}}
					>
						<span
							class={[
								"-mt-1.5",
								isMenuActive(menuItem, currentPath ?? "/")
									? "relative inline-block after:content-[''] after:absolute after:left-0 lg:after:bottom-1 after:-bottom-1 after:w-full after:h-[1.5px] after:bg-zms-blue"
									: "",
							]}
						>
							<Formatted text={menuItem.name} />
						</span>
						{#if (menuItem.submenuItems?.length ?? 0) > 0}
							<button
								aria-label="Otevřít menu"
								class="shrink-0 lg:pointer-events-none"
								onclick={(event: MouseEvent): void => {
									if (window.matchMedia("(pointer: coarse)").matches) {
										event.preventDefault();
										event.stopPropagation();
										submenuOpenMap[index] = !openSubmenu;
									}
								}}
							>
								<Icon
									icon={chevron}
									class={[
										openSubmenu ? "rotate-[270deg]" : "rotate-90",
										"w-8 sm:w-10 lg:w-auto transition-transform duration-300",
										isMenuActive(menuItem, currentPath ?? "/") ? "underline" : "",
									]}
									square
								/>
							</button>
						{/if}
					</MenuItemLink>

					{#if menuItem.submenuItems}
						<div
							class={{
								"grid transition-all overflow-clip duration-300": true,
								"grid-rows-[0fr]": !openSubmenu,
								"grid-rows-[1fr]": openSubmenu,
							}}
						>
							<div
								class={{
									"bg-white min-h-0 px-4 lg:px-[4.5rem] lg:font-medium lg:pb-14 lg:pt-12 lg:flex flex-col gap-8 lg:-left-[4.5rem] lg:origin-[4.5rem_0] lg:rounded-[1.25rem] lg:border-2 border-fstlight-100 lg:absolute lg:top-full transition-all": true,
									"lg:opacity-100 lg:pointer-events-auto lg:scale-100": openSubmenu,
									"lg:opacity-0 lg:pointer-events-none lg:scale-75": !openSubmenu,
								}}
							>
								<span class="uppercase text-xs tracking-[0.5em] text-fstlight-400 hidden lg:inline-block">
									{menuItem.name}
								</span>
								<ul
									class="grid grid-cols-1 w-full lg:w-max gap-x-14 gap-y-1 lg:gap-y-7"
									class:lg:grid-cols-2={menuItem.submenuItems.length > 1}
								>
									{#each menuItem.submenuItems as submenuItem (submenuItem)}
										<li class="tracking-[0.015em]">
											<MenuItemLink
												menuItem={submenuItem}
												class="text-right lg:text-left text-base sm:text-lg lg:text-base"
											>
												<Formatted text={submenuItem.name} />
											</MenuItemLink>
										</li>
									{/each}
								</ul>
							</div>
						</div>
					{/if}
				</li>
			{/each}
		</ul>

		<div class="px-2 sm:px-3 w-full lg:hidden flex items-center">
			<Link
				href={getCataloguePath(tenant.locale)}
				variant={ButtonVariant.BlackOutlineWithIcon}
				class={["mx-auto text-sm sm:text-base text-nowrap"]}
			>
				<TextWithIcon reversed icon={catalogueLogo}>{catalogueNameMap[tenant.locale]}</TextWithIcon>
			</Link>
		</div>

		<div class="-mt-1.5 hidden lg:flex items-center gap-4 sm:gap-6 lg:gap-[2.5rem]">
			<Link
				href={getCataloguePath(tenant.locale)}
				variant={ButtonVariant.BlackOutlineWithIcon}
				class="duration-100 h-min transition-all text-nowrap text-xs"
			>
				<TextWithIcon reversed icon={catalogueLogo}>{catalogueNameMap[tenant.locale]}</TextWithIcon>
			</Link>
			<div class="flex justify-end">
				<LanguagePicker currentLocale={tenant.locale} locales={Object.values(Locale)} {pagePath} />
			</div>
		</div>

		<div class="w-full flex justify-center lg:hidden mt-auto pt-4">
			<LanguagePicker currentLocale={tenant.locale} locales={Object.values(Locale)} {pagePath} mobileSimple />
		</div>
	</div>
</div>
